import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { FiDownload, FiFileText, FiCalendar, FiInfo, FiEye, FiX } from 'react-icons/fi';
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import './DocumentsPage.css';

const isPdf = (filename) => /\.pdf$/i.test(filename || '');
const isDocx = (filename) => /\.docx$/i.test(filename || '');
const isPreviewable = (filename) => isPdf(filename) || isDocx(filename);

// Display label for the format badge — PDF / DOCX (uppercase, short).
// Falls back to whatever extension the filename actually has so unknown
// types still get a hint (e.g. XLSX).
const fileFormatLabel = (filename) => {
    const m = (filename || '').match(/\.([a-z0-9]+)$/i);
    return m ? m[1].toUpperCase() : '';
};

// Stream a remote file into a download anchor. Used from both the card
// and from inside the viewer modal — extracted so the two paths stay
// identical (filename, error handling, blob lifecycle).
const downloadDocument = async (doc, onError) => {
    const response = await fetch(`${process.env.REACT_APP_BACK_END}${doc.download_url}`);
    if (!response.ok) {
        if (onError) onError(new Error(`HTTP ${response.status}`));
        return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = doc.filename;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const DocumentCard = ({ document, t, onPreview }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    // Get localized name and description based on current language
    const name = i18n.language === 'ru' ? document.name_ru : document.name_en;
    const description = i18n.language === 'ru' ? document.description_ru : document.description_en;
    const previewable = isPreviewable(document.filename);
    const formatLabel = fileFormatLabel(document.filename);

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            await downloadDocument(document, (err) => { throw err; });
        } catch (error) {
            console.error('Download error:', error);
            alert(t('error_unknown'));
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="document-card">
            <div className="document-icon">
                <FiFileText size={32} />
            </div>
            <div className="document-content">
                <h3 className="document-title">{name}</h3>
                <p className="document-description">{description}</p>
                <div className="document-meta">
                    <span className="document-meta-item">
                        <FiInfo size={14} />
                        {t('doc_version')}: {document.version}
                    </span>
                    <span className="document-meta-item">
                        <FiCalendar size={14} />
                        {document.date}
                    </span>
                    {formatLabel && (
                        <span className="document-meta-item document-format-badge" aria-label={formatLabel}>
                            {formatLabel}
                        </span>
                    )}
                </div>
            </div>
            <div className="document-actions">
                {previewable && (
                    <button
                        className="document-view-btn"
                        onClick={() => onPreview(document)}
                        aria-label={t('doc_view')}
                    >
                        <FiEye size={18} />
                        <span>{t('doc_view')}</span>
                    </button>
                )}
                <button
                    className="document-download-btn"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    aria-label={t('doc_download')}
                >
                    <FiDownload size={18} />
                    <span>{isDownloading ? t('loading') : t('doc_download')}</span>
                    {formatLabel && (
                        <span className="document-format-tag">{formatLabel}</span>
                    )}
                </button>
            </div>
        </div>
    );
};

const DocumentViewer = ({ document, onClose, t }) => {
    const docxContainerRef = useRef(null);
    const [docxLoading, setDocxLoading] = useState(false);
    const [docxError, setDocxError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async () => {
        if (!document) return;
        setIsDownloading(true);
        try {
            await downloadDocument(document, (err) => { throw err; });
        } catch (err) {
            console.error('Download error:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        const prevOverflow = window.document.body.style.overflow;
        window.document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            window.document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    useEffect(() => {
        if (!document || !isDocx(document.filename) || !docxContainerRef.current) return;
        let cancelled = false;
        setDocxLoading(true);
        setDocxError(null);
        const sep = document.download_url.includes('?') ? '&' : '?';
        const src = `${process.env.REACT_APP_BACK_END}${document.download_url}${sep}inline=1`;
        (async () => {
            try {
                const resp = await fetch(src);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const blob = await resp.blob();
                const { renderAsync } = await import('docx-preview');
                if (cancelled || !docxContainerRef.current) return;
                docxContainerRef.current.innerHTML = '';
                await renderAsync(blob, docxContainerRef.current, undefined, {
                    inWrapper: true,
                    ignoreWidth: false,
                    ignoreHeight: false,
                    breakPages: true,
                });
            } catch (err) {
                if (!cancelled) setDocxError(err.message || String(err));
            } finally {
                if (!cancelled) setDocxLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [document]);

    if (!document) return null;
    const name = i18n.language === 'ru' ? document.name_ru : document.name_en;
    const sep = document.download_url.includes('?') ? '&' : '?';
    const src = `${process.env.REACT_APP_BACK_END}${document.download_url}${sep}inline=1`;
    const docx = isDocx(document.filename);

    const formatLabel = fileFormatLabel(document.filename);

    return (
        <div className="document-viewer-overlay" onClick={onClose}>
            <div
                className="document-viewer"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="document-viewer-title"
            >
                <div className="document-viewer-header">
                    <h3 id="document-viewer-title">{name}</h3>
                    <div className="document-viewer-header-actions">
                        <button
                            className="document-viewer-download"
                            onClick={handleDownload}
                            disabled={isDownloading}
                            title={t('doc_download')}
                        >
                            <FiDownload size={16} />
                            <span>{isDownloading ? t('loading') : t('doc_download')}</span>
                            {formatLabel && (
                                <span className="document-format-tag">{formatLabel}</span>
                            )}
                        </button>
                        <button className="document-viewer-close" onClick={onClose} title={t('close')} aria-label={t('close')}>
                            <FiX size={20} />
                        </button>
                    </div>
                </div>
                {docx ? (
                    <div className="document-viewer-docx-wrap">
                        {docxLoading && <div className="document-viewer-status">{t('loading')}</div>}
                        {docxError && <div className="document-viewer-status document-viewer-error">{docxError}</div>}
                        <div ref={docxContainerRef} className="document-viewer-docx" />
                    </div>
                ) : (
                    <iframe
                        title={name}
                        src={src}
                        className="document-viewer-frame"
                    />
                )}
            </div>
        </div>
    );
};

const Documents = () => {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACK_END}/documents/`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setDocuments(data.documents || []);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="documents-page">
            <div className="documents-header">
                <h1>{t('documents_title')}</h1>
                <p>{t('documents_description')}</p>
            </div>

            {isLoading ? (
                <LoadingSpinner message="loading_data" size="md" />
            ) : error ? (
                <ErrorMessage
                    type="error"
                    message="error_loading_data"
                    details={error}
                    onRetry={fetchDocuments}
                />
            ) : documents.length === 0 ? (
                <div className="documents-empty">
                    <FiFileText size={48} />
                    <p>{t('documents_empty')}</p>
                </div>
            ) : (
                <div className="documents-groups">
                    {(() => {
                        const groupOrder = ['regulations', 'services', 'forms', 'other'];
                        const groups = documents.reduce((acc, doc) => {
                            const cat = doc.category || 'other';
                            (acc[cat] = acc[cat] || []).push(doc);
                            return acc;
                        }, {});
                        return groupOrder
                            .filter(cat => groups[cat] && groups[cat].length > 0)
                            .map(cat => (
                                <section key={cat} className="documents-group">
                                    <h2 className="documents-group-title">{t(`doc_category_${cat}`)}</h2>
                                    <div className="documents-list">
                                        {groups[cat].map(doc => (
                                            <DocumentCard
                                                key={doc.id}
                                                document={doc}
                                                t={t}
                                                onPreview={setPreviewDoc}
                                            />
                                        ))}
                                    </div>
                                </section>
                            ));
                    })()}
                </div>
            )}

            {previewDoc && (
                <DocumentViewer
                    document={previewDoc}
                    onClose={() => setPreviewDoc(null)}
                    t={t}
                />
            )}
        </div>
    );
};

export default Documents;
